import React, { useMemo, useState } from 'react';
import { Copy, Plus, Settings, Trash2 } from 'lucide-react';
import {
  IdealWeekTemplate,
  IdealDaySession,
  SessionCategory,
  SessionDiscipline,
  SessionTemplate,
  ZoneType,
} from '../../types/athlete';

interface StepIdealWeekProps {
  idealWeekTemplate: IdealWeekTemplate;
  sessionTemplates: SessionTemplate[];
  onUpdateIdealWeek: (template: IdealWeekTemplate) => void;
  onUpdateTemplates: (templates: SessionTemplate[]) => void;
}

const daysOfWeek = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

const timeOptions = [
  '5AM',
  '6AM',
  '7AM',
  '8AM',
  '9AM',
  '10AM',
  '11AM',
  '12PM',
  '1PM',
  '2PM',
  '3PM',
  '4PM',
  '5PM',
  '6PM',
  '7PM',
  '8PM',
  '9PM',
];

const categoryOptions: SessionCategory[] = ['recovery', 'endurance', 'tempo', 'threshold', 'interval', 'brick', 'test', 'custom'];
const disciplineOptions: SessionDiscipline[] = ['swim', 'bike', 'run', 'strength'];
const zoneOptions: ZoneType[] = ['none', 'E', 'L', 'M', 'T', 'I', 'Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7', 'CSS', 'CSS+X', 'CSS-X'];

const createSessionId = (): string => `ideal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const parseOrderTime = (value: string): number => {
  const match = value.match(/(\d+)(AM|PM)/i);
  if (!match) return 0;
  let hours = parseInt(match[1], 10);
  const period = match[2].toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return hours * 60;
};

const StepIdealWeek: React.FC<StepIdealWeekProps> = ({
  idealWeekTemplate,
  sessionTemplates,
  onUpdateIdealWeek,
  onUpdateTemplates,
}) => {
  const [copySourceDay, setCopySourceDay] = useState<Record<number, number>>({});
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SessionTemplate | null>(null);
  const [draftTemplate, setDraftTemplate] = useState<SessionTemplate | null>(null);
  const [draftSessionByDay, setDraftSessionByDay] = useState<Record<number, { templateId: string; orderTime: string; durationOverride: string }>>({});

  const templatesById = useMemo(() => {
    return sessionTemplates.reduce((acc, template) => {
      acc[template.id] = template;
      return acc;
    }, {} as Record<string, SessionTemplate>);
  }, [sessionTemplates]);

  const customTemplateId = useMemo(() => {
    const customTemplate = sessionTemplates.find(template => template.category === 'custom' || template.name === 'Custom Session');
    return customTemplate?.id || sessionTemplates[0]?.id || '';
  }, [sessionTemplates]);

  const daySessions = (day: number): IdealDaySession[] => {
    return idealWeekTemplate.days[day] || [];
  };

  const updateDaySessions = (day: number, sessions: IdealDaySession[]) => {
    onUpdateIdealWeek({
      ...idealWeekTemplate,
      days: {
        ...idealWeekTemplate.days,
        [day]: sessions,
      },
    });
  };

  const addSession = (day: number) => {
    const draft = draftSessionByDay[day] || {
      templateId: sessionTemplates[0]?.id || '',
      orderTime: '6AM',
      durationOverride: '',
    };

    if (!draft.templateId) return;

    const newSession: IdealDaySession = {
      id: createSessionId(),
      templateId: draft.templateId,
      orderTime: draft.orderTime,
      durationOverride: draft.durationOverride ? parseInt(draft.durationOverride, 10) : undefined,
    };

    const updated = [...daySessions(day), newSession].sort((a, b) => parseOrderTime(a.orderTime) - parseOrderTime(b.orderTime));
    updateDaySessions(day, updated);
  };

  const updateSession = (day: number, sessionId: string, updates: Partial<IdealDaySession>) => {
    const updated = daySessions(day).map(session =>
      session.id === sessionId ? { ...session, ...updates } : session
    );
    updateDaySessions(day, updated.sort((a, b) => parseOrderTime(a.orderTime) - parseOrderTime(b.orderTime)));
  };

  const removeSession = (day: number, sessionId: string) => {
    updateDaySessions(day, daySessions(day).filter(session => session.id !== sessionId));
  };

  const handleCopyDay = (targetDay: number) => {
    const sourceDay = copySourceDay[targetDay];
    if (!sourceDay || sourceDay === targetDay) return;

    const copied = daySessions(sourceDay).map(session => ({
      ...session,
      id: createSessionId(),
    }));

    updateDaySessions(targetDay, copied);
  };

  const handleTemplateDelete = (templateId: string) => {
    if (templateId === customTemplateId) return;
    const updatedTemplates = sessionTemplates.filter(template => template.id !== templateId);
    onUpdateTemplates(updatedTemplates);

    const updatedDays = Object.entries(idealWeekTemplate.days).reduce((acc, [dayKey, sessions]) => {
      acc[Number(dayKey)] = sessions.map(session =>
        session.templateId === templateId
          ? { ...session, templateId: customTemplateId }
          : session
      );
      return acc;
    }, {} as Record<number, IdealDaySession[]>);

    onUpdateIdealWeek({
      ...idealWeekTemplate,
      days: updatedDays,
    });
  };

  const startTemplateEdit = (template: SessionTemplate) => {
    setEditingTemplate(template);
    setDraftTemplate({ ...template });
  };

  const startTemplateCreate = () => {
    setEditingTemplate(null);
    setDraftTemplate({
      id: `template-${Date.now()}`,
      name: 'New Session',
      discipline: 'run',
      category: 'custom',
      zone_type: 'none',
      zone_value: 0,
      min_duration: 30,
      max_duration: 30,
      is_long_session: false,
      is_brick: false,
      brick_run_zone: null,
      brick_run_duration: null,
      has_run_walk: false,
      run_walk_ratio: '9:1',
      race_types: ['sprint', 'olympic', '70.3', 'ironman'],
      experience_levels: ['beginner', 'intermediate', 'advanced'],
      placement_rules: {},
      description: 'Custom session template',
    });
  };

  const saveTemplate = () => {
    if (!draftTemplate) return;

    const updatedTemplates = editingTemplate
      ? sessionTemplates.map(template =>
          template.id === draftTemplate.id ? draftTemplate : template
        )
      : [...sessionTemplates, draftTemplate];

    onUpdateTemplates(updatedTemplates);
    setEditingTemplate(null);
    setDraftTemplate(null);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-2">Ideal Week Builder</h2>
        <p className="text-muted">Plan a perfect week. We will replicate and scale it across your entire plan.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
        {daysOfWeek.map(day => (
          <div key={day.value} className="card-base p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">{day.label}</h3>
              <div className="flex items-center space-x-2">
                <select
                  value={copySourceDay[day.value] || ''}
                  onChange={(event) =>
                    setCopySourceDay(prev => ({
                      ...prev,
                      [day.value]: Number(event.target.value),
                    }))
                  }
                  className="bg-[#0f172a] text-xs"
                >
                  <option value="">Copy from...</option>
                  {daysOfWeek.filter(source => source.value !== day.value).map(source => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => handleCopyDay(day.value)}
                  className="p-1 text-muted hover:text-white"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {daySessions(day.value).map(session => {
                const template = templatesById[session.templateId];
                const durationValue = session.durationOverride ?? template?.min_duration ?? 30;
                return (
                  <div key={session.id} className="bg-[#0f172a] p-3 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{template?.name || 'Session'}</p>
                      <button
                        type="button"
                        onClick={() => removeSession(day.value, session.id)}
                        className="text-muted hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={session.orderTime}
                        onChange={(event) => updateSession(day.value, session.id, { orderTime: event.target.value })}
                        className="text-xs"
                      >
                        {timeOptions.map(time => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min={15}
                        value={durationValue}
                        onChange={(event) =>
                          updateSession(day.value, session.id, {
                            durationOverride: parseInt(event.target.value, 10),
                          })
                        }
                        className="text-xs"
                      />
                    </div>
                    <p className="text-xs text-muted capitalize">{template?.discipline || 'custom'}</p>
                  </div>
                );
              })}
            </div>

            <div className="space-y-2 border-t border-[#1e293b] pt-3">
              <select
                value={draftSessionByDay[day.value]?.templateId || sessionTemplates[0]?.id || ''}
                onChange={(event) =>
                  setDraftSessionByDay(prev => ({
                    ...prev,
                    [day.value]: {
                      templateId: event.target.value,
                      orderTime: prev[day.value]?.orderTime || '6AM',
                      durationOverride: prev[day.value]?.durationOverride || '',
                    },
                  }))
                }
                className="text-xs"
              >
                {sessionTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={draftSessionByDay[day.value]?.orderTime || '6AM'}
                  onChange={(event) =>
                    setDraftSessionByDay(prev => ({
                      ...prev,
                      [day.value]: {
                        templateId: prev[day.value]?.templateId || sessionTemplates[0]?.id || '',
                        orderTime: event.target.value,
                        durationOverride: prev[day.value]?.durationOverride || '',
                      },
                    }))
                  }
                  className="text-xs"
                >
                  {timeOptions.map(time => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={15}
                  placeholder="Duration"
                  value={draftSessionByDay[day.value]?.durationOverride || ''}
                  onChange={(event) =>
                    setDraftSessionByDay(prev => ({
                      ...prev,
                      [day.value]: {
                        templateId: prev[day.value]?.templateId || sessionTemplates[0]?.id || '',
                        orderTime: prev[day.value]?.orderTime || '6AM',
                        durationOverride: event.target.value,
                      },
                    }))
                  }
                  className="text-xs"
                />
              </div>
              <button
                type="button"
                onClick={() => addSession(day.value)}
                className="flex items-center justify-center space-x-2 w-full py-2 text-xs font-medium bg-[#1e293b] hover:bg-[#2d3748] rounded-lg"
              >
                <Plus size={14} />
                <span>Add session</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card-base p-6">
        <button
          type="button"
          onClick={() => setShowTemplateManager(prev => !prev)}
          className="flex items-center space-x-2 text-sm font-semibold"
        >
          <Settings size={16} />
          <span>Manage Session Types</span>
        </button>

        {showTemplateManager && (
          <div className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted">Edit, add, or remove session templates.</p>
              <button
                type="button"
                onClick={startTemplateCreate}
                className="flex items-center space-x-2 px-3 py-1.5 bg-[#1e293b] rounded-lg text-xs"
              >
                <Plus size={14} />
                <span>New template</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sessionTemplates.map(template => (
                <div key={template.id} className="bg-[#0f172a] rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{template.name}</p>
                      <p className="text-xs text-muted capitalize">{template.discipline} • {template.category}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => startTemplateEdit(template)}
                        className="text-xs text-[#a855f7]"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTemplateDelete(template.id)}
                        className="text-xs text-red-400"
                        disabled={template.id === customTemplateId}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-muted">Default duration: {template.min_duration} min</p>
                </div>
              ))}
            </div>

            {draftTemplate && (
              <div className="bg-[#0f172a] rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-muted mb-1">Name</label>
                    <input
                      type="text"
                      value={draftTemplate.name}
                      onChange={(event) => setDraftTemplate({ ...draftTemplate, name: event.target.value })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted mb-1">Discipline</label>
                    <select
                      value={draftTemplate.discipline}
                      onChange={(event) => setDraftTemplate({ ...draftTemplate, discipline: event.target.value as SessionDiscipline })}
                      className="w-full"
                    >
                      {disciplineOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-muted mb-1">Category</label>
                    <select
                      value={draftTemplate.category}
                      onChange={(event) => setDraftTemplate({ ...draftTemplate, category: event.target.value as SessionCategory })}
                      className="w-full"
                    >
                      {categoryOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-muted mb-1">Default duration (min)</label>
                    <input
                      type="number"
                      min={15}
                      value={draftTemplate.min_duration}
                      onChange={(event) =>
                        setDraftTemplate({
                          ...draftTemplate,
                          min_duration: parseInt(event.target.value, 10),
                          max_duration: parseInt(event.target.value, 10),
                        })
                      }
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted mb-1">Zone type</label>
                    <select
                      value={draftTemplate.zone_type}
                      onChange={(event) => setDraftTemplate({ ...draftTemplate, zone_type: event.target.value as ZoneType })}
                      className="w-full"
                    >
                      {zoneOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-muted mb-1">Zone value</label>
                    <input
                      type="number"
                      value={draftTemplate.zone_value}
                      onChange={(event) => setDraftTemplate({ ...draftTemplate, zone_value: parseInt(event.target.value, 10) })}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setDraftTemplate(null)}
                    className="px-4 py-2 text-xs text-muted"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveTemplate}
                    className="px-4 py-2 text-xs bg-[#a855f7] text-white rounded-lg"
                  >
                    Save template
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StepIdealWeek;
